import { Injectable, inject } from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {DeveloperApiService} from './api/routes/developer-api.service';
import {ProjectAccessLevel} from '../../canvas/enums/project-access-level.enum';
import {SettingsService} from '../../canvas/services/settings.service';

@Injectable({
  providedIn: 'root'
})
export class NewProjectGuardService implements CanActivate {
  private router = inject(Router);
  private developerApi = inject(DeveloperApiService);
  private settingsService = inject(SettingsService);

  /**
   * An integrator's per-visitor link: /app/projects/guest/new?token=...
   *
   * This used to be `new Promise(async (resolve) => { ... return false })`.
   * Returning from an executor resolves nothing, so the router waited forever:
   * on success the page reloaded itself out of it, and on ANY failure -- an
   * expired token, a lapsed subscription, the budget -- the visitor got a
   * blank page and the error went to an empty catch.
   */
  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const params = route.queryParams;
    const token = (params.token || '').trim();
    if (!token) {
      return this.router.parseUrl('/guest/unavailable?code=guest_link_not_found');
    }
    const displayName = params.guest_name ? String(params.guest_name) : '';

    const projectRequest: any = {
      name: params.name ? params.name : '',
      description: params.description ? params.description : '',
      access_level: ProjectAccessLevel.OPEN_GUEST,
      comments_enabled: true,
    };
    if (displayName) {
      projectRequest.guestName = displayName;
    }
    if (params.guest_email) {
      projectRequest.guestEmail = params.guest_email;
    }
    if (params.guest_phone) {
      projectRequest.guestPhone = params.guest_phone;
    }
    if (params.folder_id) {
      projectRequest.parentId = params.folder_id;
    }

    try {
      const project = await this.developerApi.createNewProject(token, projectRequest, false);
      // Kept with the project it opened: CanvasAuthGuardService uses it for
      // that project only, not for every project opened in this browser.
      this.settingsService.setLocalValue('accessToken', JSON.stringify({projectId: project.id, token}));

      // Every value encoded: a guest named "Smith & Sons" used to end the
      // query string at the ampersand.
      const query = new URLSearchParams({token});
      if (displayName) {
        query.set('displayName', displayName);
      }
      // A full load, and `replace` so Back does not return here and start
      // -- and bill -- a second project.
      window.location.replace(`/app/p/${encodeURIComponent(project.id)}?${query.toString()}`);
      return false;
    } catch (e) {
      const code = (e && e.msg && e.msg.errorCode) || '';
      return this.router.parseUrl('/guest/unavailable?code=' + encodeURIComponent(code));
    }
  }
}
